import type { StateConfig, StateDefinition } from '@/entities/state/model'
import { dashboardLayoutThreeColumnWithBottom } from './layoutVariants.mocks'

export const stateDefinitionConfigMock: StateConfig = {
  hsFieldsPriority: [
    'Customer Name',
    'Order Number',
    'Collection Window',
    'Van Seq',
    'Ambient',
    'Bulk Ambient',
    'Chilled',
    'Bulk Chilled',
    'Frozen',
    'Hot Food',
    'Restricted',
    'Order Type',
  ],
  features: {
    realtimeUpdates: true,
    hideSearchBar: false,
    waitTime: {
      endState: ['Completed'],
      startState: ['Arrived', 'DeliveringToCustomer'],
      alert: 3000, // 5 minutes in seconds - warning threshold
      goal: 6000, // 10 minutes in seconds - error threshold
    },
    orderGrouping: {
      enabled: true,
      groupPrefix: 'group',
    },
  },
  categories: [
    'Delivering to Customer',
    'Arrived',
    'On The Way',
    'Ready To Pick',
    'Ready To Collect',
  ],
  orderCardFields: [
    'vanNumber',
    'sequenceNumber',
    'orderType',
    'driverName',
  ],
  states: [
    {
      canForceTransition: false,
      displayState: null,
      isError: false,
      shouldNotify: false,
      progressionLevel: 1,
      canAcknowledge: false,
      eventTriggers: ['REGISTRATION'],
      name: 'Registered',
      isActive: true,
      analyticsCategory: 'registered',
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'ON THE WAY',
        color: '#8523f4',
        categoryTitle: 'On The Way',
        orderDetailsSummary: '{customerName} is on the way',
        stateDisplayName: 'On The Way',
        showCollectionTime: true,
      },
      isError: false,
      shouldNotify: true,
      progressionLevel: 10,
      canAcknowledge: false,
      eventTriggers: ['NO_UPDATES_5_MINS', 'TEMPO_TIMEOUT', 'onTheWay'],
      name: 'OnTheWay',
      isActive: true,
      analyticsCategory: 'onTheWay',
    },
    {
      canForceTransition: false,
      displayState: {
        orderDetailsTitle: '{etaMinutes} AWAY',
        color: '#8523f4',
        categoryTitle: 'On The Way',
        orderDetailsSummary: '{customerName} is arriving {etaDirectionPreposition} {arrivalTime}.',
        stateDisplayName: 'On The Way',
      },
      isError: false,
      shouldNotify: true,
      progressionLevel: 10,
      canAcknowledge: false,
      eventTriggers: ['TEMPO_UPDATE'],
      name: 'TempoOnTheWay',
      isActive: true,
      analyticsCategory: 'onTheWay',
    },
    {
      canForceTransition: false,
      displayState: {
        orderDetailsTitle: '{etaMinutes} AWAY',
        color: '#343a40',
        categoryTitle: 'On The Way',
        orderDetailsSummary: 'Driver is arriving {etaDirectionPreposition} {arrivalTime}.',
        stateDisplayName: 'On The Way',
      },
      isError: false,
      shouldNotify: true,
      progressionLevel: 10,
      canAcknowledge: false,
      eventTriggers: ['PARTNER_ETA'],
      name: 'PartnerOnTheWay',
      isActive: true,
      analyticsCategory: 'onTheWay',
    },
    {
      canForceTransition: false,
      displayState: {
        orderDetailsTitle: 'STOPPED',
        color: '#FA5252',
        categoryTitle: 'On The Way',
        orderDetailsSummary: '{customerName} stopped tracking journey at {formattedLastTransitionTime}',
        stateDisplayName: 'Stopped',
        quickButtons: [
          {
            action: 'ACKNOWLEDGE',
            label: 'Clear',
          },
        ],
      },
      isError: true,
      shouldNotify: true,
      progressionLevel: 15,
      canAcknowledge: true,
      eventTriggers: ['TEMPO_CANCELLED'],
      name: 'Stopped',
      isActive: false,
      analyticsCategory: 'haltFail',
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'CANCELLED',
        color: '#FA5252',
        categoryTitle: 'On The Way',
        orderDetailsSummary: '{customerName}\'s order was cancelled',
        stateDisplayName: 'Cancelled',
        quickButtons: [
          {
            action: 'ACKNOWLEDGE',
            label: 'Clear',
          },
        ],
      },
      isError: true,
      shouldNotify: true,
      progressionLevel: 25,
      canAcknowledge: false,
      eventTriggers: ['cancelled'],
      name: 'Cancelled',
      isActive: false,
      analyticsCategory: 'haltFail',
    },
    {
      displayState: {
        orderDetailsTitle: 'CUSTOMER ARRIVED',
        color: '#297d2f',
        orderDetailsSummary: '{customerName} arrived at {formattedLastTransitionTime}.',
        categoryTitle: 'Arrived',
        showSuccessMessage: false,
        stateDisplayName: 'Arrived',
        quickButtons: [
          {
            action: 'DeliveringToCustomer',
            label: 'We\'re on it',
          },
        ],
      },
      canForceTransition: true,
      isError: false,
      shouldNotify: true,
      progressionLevel: 20,
      canAcknowledge: true, // This enables animation for unacknowledged orders
      eventTriggers: ['GEOTRIGGER', 'arrival'],
      name: 'Arrived',
      isActive: true,
      analyticsCategory: 'arrived',
    },
    {
      displayState: {
        orderDetailsTitle: 'DELIVERING TO CUSTOMER',
        color: '#343a40',
        orderDetailsSummary: '{customerName} arrived at {formattedWaitTimeStart}.',
        categoryTitle: 'Delivering to Customer',
        showSuccessMessage: true,
        stateDisplayName: 'Delivering',
        quickButtons: [
          {
            action: 'Completed',
            label: 'Complete',
          },
        ],
      },
      canForceTransition: true,
      isError: false,
      shouldNotify: false,
      progressionLevel: 21,
      canAcknowledge: true, // Changed to true to enable animation for this state
      eventTriggers: ['deliveringToCustomer'],
      name: 'DeliveringToCustomer',
      isActive: true,
      analyticsCategory: 'arrived',
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'COMPLETED',
        color: '#343a40',
        categoryTitle: 'Arrived',
        orderDetailsSummary: '{customerName}\'s order is completed.',
        stateDisplayName: 'Completed',
      },
      isError: false,
      shouldNotify: false,
      isVerified: false,
      progressionLevel: 100,
      canAcknowledge: false,
      eventTriggers: ['completed'],
      name: 'Completed',
      isActive: false,
      analyticsCategory: 'haltSucceed',
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'READY TO COLLECT',
        color: '#343a40',
        categoryTitle: 'Ready To Collect',
        orderDetailsSummary: '{customerName}\'s order is ready to collect.',
        stateDisplayName: 'Ready To Collect',
      },
      isError: false,
      shouldNotify: false,
      progressionLevel: 5,
      canAcknowledge: false,
      eventTriggers: ['pickCompleted'],
      name: 'ReadyToCollect',
      isActive: true,
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'READY TO PICK',
        color: '#FA5252',
        categoryTitle: 'Ready To Pick',
        orderDetailsSummary: '{customerName}\'s order is ready to pick.',
        stateDisplayName: 'Ready To Pick',
        quickButtons: [
          {
            action: 'ReadyToCollect',
            label: 'Pick Complete',
          },
        ],
      },
      isError: false,
      shouldNotify: true,
      progressionLevel: 3,
      canAcknowledge: false,
      eventTriggers: ['readyToPick'],
      name: 'ReadyToPick',
      isActive: true,
    },
    {
      canForceTransition: false,
      displayState: {
        orderDetailsTitle: 'COMPLETED',
        color: '#37B24D',
        categoryTitle: 'Completed',
        orderDetailsSummary: '{customerName}\'s order has been completed.',
        stateDisplayName: 'Completed',
      },
      isError: false,
      shouldNotify: false,
      progressionLevel: 100,
      canAcknowledge: false,
      eventTriggers: ['completed'],
      name: 'Completed',
      isActive: true,
    },
  ],
  verificationErrorCodes: [
    {
      errorCode: 'FRIEND',
      description: 'Order is for someone else (relative/friend)',
      shouldDisplay: true,
    },
    {
      errorCode: 'BUSINESS',
      description: 'Order is for a business or organisation',
      shouldDisplay: true,
    },
    {
      errorCode: 'DONT-KNOW',
      description: 'Customer can\'t remember or doesn\'t know.',
      shouldDisplay: true,
    },
    {
      errorCode: 'UNWILLING',
      description: 'Customer is unwilling to provide.',
      shouldDisplay: true,
    },
    {
      errorCode: 'OTHER',
      description: 'Other',
      shouldDisplay: true,
    },
  ],
  // The dashboard layout is config-driven; `dev:mock` and the success handlers
  // serve this shared example so the mocked dashboard exercises the full dynamic
  // surface — three middle columns plus a bottom carousel region. `ReadyToPick`
  // also feeds the sidebar order panel below to show the two regions coexisting.
  layout: dashboardLayoutThreeColumnWithBottom,
  orderPanels: [
    { id: 'ready-to-pick', label: 'Ready to pick', states: ['ReadyToPick'] },
  ],
}

export function createStateDictionaryMock(
  states: StateConfig['states'] = stateDefinitionConfigMock.states,
): Record<string, StateDefinition> {
  if (states !== stateDefinitionConfigMock.states) {
    const stateNames = states.map(state => state.name)
    const duplicateStateName = stateNames.find((name, index) => stateNames.indexOf(name) !== index)

    if (duplicateStateName) {
      throw new Error(`Duplicate state name in state dictionary mock: ${duplicateStateName}`)
    }
  }

  return Object.fromEntries(
    states.map(state => [state.name, state as StateDefinition]),
  )
}

export const stateDictionaryMock = createStateDictionaryMock()
